import { Text } from '@mantine/core';
import { useCallback, useMemo, useState } from 'react';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { ActionContext } from '@dealstack/frontend/app/pages/Valuations/components/table/cells/ActionContext';
import { ROW_HEIGHT } from '@dealstack/frontend/app/pages/Valuations/components/table/constants';
import { TruncateMiddle } from '@dealstack/frontend/app/pages/Valuations/components/EntitySelectorDropdown/TruncateMiddle/TruncateMiddle';
import { TextWithPotentialIndicator } from '@dealstack/frontend/app/pages/Valuations/components/table/cells/styled';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { RecordAny } from '@dealstack/shared/lib/common/common';

interface Props {
	value: string | number | null;
	alignEnd?: boolean;
	paddingLeft?: string;
	padding?: string;
	formatter?: (value: unknown) => string;
	isHeader?: boolean;
	isSubColumn?: boolean;
	isLastSubColumn?: boolean;
	alignment?: 'start' | 'end';
	isLastRow?: boolean;
	isLastColumn?: boolean;
	row?: {
		original: {
			metadata?: {
				type?: string;
				isTotal?: boolean;
				isSummingRow?: boolean;
			};
			subRows?: unknown[];
		};
		getIsExpanded: () => boolean;
	};
	disableBorders?: boolean;
	truncateMiddle?: boolean;
	shouldHideValue?: boolean;
	indicator?: boolean;
	columnIndex?: number;
	italic?: boolean;
	renderActions?: (
		row: RecordAny,
		value: unknown,
		isHovered: boolean,
		formatter?: (value: unknown) => string
	) => React.ReactNode;
}

export const NewDefaultCell = ({
	value,
	alignEnd,
	paddingLeft,
	padding,
	formatter,
	isHeader,
	isSubColumn,
	isLastSubColumn,
	alignment,
	isLastRow,
	isLastColumn,
	row,
	disableBorders = false,
	truncateMiddle = false,
	shouldHideValue = false,
	indicator = false,
	columnIndex,
	italic,
	renderActions,
}: Props) => {
	const [isHovered, setIsHovered] = useState(false);
	const [hasActiveAction, setHasActiveAction] = useState(false);

	const isNonSummingTotalRow =
		row?.original?.metadata?.isTotal && !row?.original?.metadata?.isSummingRow;

	let backgroundColor: string;
	let hoverBackgroundColor: string | undefined;
	if (isHeader) {
		backgroundColor = isSubColumn
			? defaultTheme.v3.colors.gray[4]
			: defaultTheme.v3.colors.gray[2];
	} else if (isNonSummingTotalRow) {
		backgroundColor = defaultTheme.v3.colors.gray[0];
		hoverBackgroundColor = defaultTheme.v3.colors.gray[1];
	} else if (isSubColumn) {
		backgroundColor = defaultTheme.v3.colors.gray[0];
		hoverBackgroundColor = defaultTheme.v3.colors.gray[1];
	} else {
		backgroundColor = defaultTheme.v3.colors.white[0];
		hoverBackgroundColor = defaultTheme.v3.colors.gray[0];
	}

	let borderRight: string;
	if (
		disableBorders ||
		isNonSummingTotalRow ||
		isLastColumn ||
		(isSubColumn && !isLastSubColumn)
	) {
		borderRight = 'none';
	} else {
		borderRight = `1px solid ${defaultTheme.v3.colors.gray[4]}`;
	}

	let borderBottom: string;
	if (disableBorders || isLastRow) {
		borderBottom = 'none';
	} else {
		borderBottom = `1px solid ${defaultTheme.v3.colors.gray[4]}`;
	}

	const justifyContent = alignment === 'end' || alignEnd ? 'flex-end' : 'flex-start';

	const containerStyle: React.CSSProperties = {
		display: 'flex',
		alignItems: 'center',
		width: '100%',
		padding: padding ?? '1rem',
		gap: '0.5rem',
		textOverflow: 'ellipsis',
		overflow: 'hidden',
		paddingLeft: paddingLeft ?? '1rem',
		backgroundColor: isHovered && hoverBackgroundColor ? hoverBackgroundColor : backgroundColor,
		borderRight,
		borderBottom,
		justifyContent,
		...(isHeader ? {} : { maxHeight: `${ROW_HEIGHT}px` }),
		...(italic ? { fontStyle: 'italic' as const } : {}),
	};

	const hasSubRows = !!(row?.original?.subRows && row.original.subRows.length > 0);
	const isExpanded = row?.getIsExpanded?.() ?? false;
	const shouldHide = shouldHideValue || (hasSubRows && isExpanded);

	const renderContent = () => {
		const textSize = isHeader ? 'B6Bold' : 'B6Medium';
		const fontWeight = isNonSummingTotalRow ? 600 : undefined;

		if (shouldHide) {
			return null;
		}

		if (formatter) {
			return (
				<Text size={textSize} variant="B6Medium" fw={fontWeight}>
					{formatter(value)}
				</Text>
			);
		}
		if (truncateMiddle) {
			return <TruncateMiddle text={`${value}`} size={textSize} fw={fontWeight} />;
		}
		return (
			<Text size={textSize} variant="B6Medium" fw={fontWeight}>
				<TextWithPotentialIndicator
					$indicator={indicator}
				>{`${value}`}</TextWithPotentialIndicator>
			</Text>
		);
	};

	// Reset hover when an action closes so the cell doesn't stay
	// visually hovered after the popover portal unmounts
	const handleActionOpenChange = useCallback((open: boolean) => {
		setHasActiveAction(open);
		if (!open) {
			setIsHovered(false);
		}
	}, []);
	const actionContextValue = useMemo(
		() => ({ isActionOpen: hasActiveAction, onOpenChange: handleActionOpenChange }),
		[hasActiveAction, handleActionOpenChange]
	);
	const actions =
		!shouldHide && row?.original
			? renderActions?.(row.original, value, isHovered, formatter)
			: null;

	return (
		<div
			data-testid={TEST_ELEMENT_IDS.NEW_DEFAULT_CELL}
			data-testindex={columnIndex}
			style={containerStyle}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			<ActionContext.Provider value={actionContextValue}>
				{actions && (
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							marginRight: 'auto',
							gap: '0.25rem',
						}}
					>
						{actions}
					</div>
				)}
			</ActionContext.Provider>
			{renderContent()}
		</div>
	);
};
